import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import type { UserAgentSkill } from "@/types/workflowEngine";
import { INTEGRATION_SCOPE_OPTIONS } from "@/lib/agentSkillConstants";

export type SkillFormValues = {
  name: string;
  description: string;
  instructions: string;
  integrationScopes: string[];
  outputSchema?: Record<string, unknown>;
};

export const defaultSkillFormValues: SkillFormValues = {
  name: "",
  description: "",
  instructions: "",
  integrationScopes: [],
};

export function skillToFormValues(skill: UserAgentSkill): SkillFormValues {
  return {
    name: skill.name,
    description: skill.description ?? "",
    instructions: skill.instructions,
    integrationScopes: skill.integrationScopes ?? [],
    outputSchema:
      skill.outputSchema && typeof skill.outputSchema === "object"
        ? (skill.outputSchema as Record<string, unknown>)
        : undefined,
  };
}

export function validateSkillForm(
  values: SkillFormValues & { outputSchemaDraftInvalid?: boolean }
): string | null {
  if (!values.name.trim()) return "Skill name is required";
  if (!values.instructions.trim()) return "Instructions are required";
  if (values.outputSchemaDraftInvalid) return "Output schema must be valid JSON";
  return null;
}

type SkillBuildFormProps = {
  value: SkillFormValues & { outputSchemaDraftInvalid?: boolean };
  onChange: (patch: Partial<SkillFormValues> & { outputSchemaDraftInvalid?: boolean }) => void;
};

export function SkillBuildForm({ value, onChange }: SkillBuildFormProps) {
  const [schemaDraft, setSchemaDraft] = useState<string | null>(null);

  const outputSchemaText =
    schemaDraft ??
    (value.outputSchema && Object.keys(value.outputSchema).length > 0
      ? JSON.stringify(value.outputSchema, null, 2)
      : "");

  const toggleScope = (scope: string) => {
    const cur = value.integrationScopes;
    onChange({
      integrationScopes: cur.includes(scope) ? cur.filter((s) => s !== scope) : [...cur, scope],
    });
  };

  const handleSchemaChange = (text: string) => {
    setSchemaDraft(text);
    if (!text.trim()) {
      onChange({ outputSchema: undefined, outputSchemaDraftInvalid: false });
      return;
    }
    try {
      const parsed = JSON.parse(text) as Record<string, unknown>;
      onChange({ outputSchema: parsed, outputSchemaDraftInvalid: false });
    } catch {
      onChange({ outputSchemaDraftInvalid: true });
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="skill-name">Name</Label>
        <Input
          id="skill-name"
          value={value.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="e.g., Spam Classifier"
          data-testid="input-skill-name"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="skill-description">Description</Label>
        <Input
          id="skill-description"
          value={value.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Short summary of what this skill does"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="skill-instructions">Instructions</Label>
        <Textarea
          id="skill-instructions"
          value={value.instructions}
          onChange={(e) => onChange({ instructions: e.target.value })}
          placeholder="Tell the model how to think and what to output..."
          rows={10}
          className="font-mono text-sm"
          data-testid="textarea-skill-instructions"
        />
      </div>
      <div className="space-y-2">
        <Label>Integration scopes</Label>
        <div className="flex flex-wrap gap-2">
          {INTEGRATION_SCOPE_OPTIONS.map((s) => (
            <Badge
              key={s}
              variant={value.integrationScopes.includes(s) ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => toggleScope(s)}
            >
              {s}
            </Badge>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label>Output schema (JSON, optional)</Label>
        <Textarea
          value={outputSchemaText}
          onChange={(e) => handleSchemaChange(e.target.value)}
          placeholder='{"type":"object","properties":{...}}'
          rows={5}
          className="font-mono text-xs"
        />
        {value.outputSchemaDraftInvalid && (
          <p className="text-xs text-destructive">Invalid JSON</p>
        )}
      </div>
    </div>
  );
}
